import { Dropdown, Toolbar } from '@bynder/design-system';
import type { Editor } from '@tiptap/core';
import type { Level } from '@tiptap/extension-heading';
import { DEFAULT_TEXT_STYLE_OPTIONS } from '../../data/configs/textStyles';
import type { TextStyle } from '../../data/configs/textStyles';

interface TextStyleOptionsDropdownProps {
  editor: Editor;
  textStyles?: TextStyle[];
}

export default function TextStyleOptionsDropdown({
  editor,
  textStyles,
}: TextStyleOptionsDropdownProps) {
  const textStyleOptions = textStyles || DEFAULT_TEXT_STYLE_OPTIONS;

  let dropdownPlaceholderLabel = 'Select a text size';

  const activeTextStyleOption = editor.isActive('paragraph')
    ? textStyleOptions.find((option) => option.level === 0)
    : editor.isActive('heading')
      ? textStyleOptions.find(
          (option) => option.level === editor.getAttributes('heading')?.level,
        )
      : undefined;

  if (activeTextStyleOption) {
    dropdownPlaceholderLabel = activeTextStyleOption.label;
  }

  const textStyleDropdownItems = textStyleOptions.map((option) => {
    return (
      <Dropdown.ListItem
        // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
        role="option"
        key={option.level}
        onClick={() => {
          option.level === 0
            ? editor?.chain().focus().setParagraph().run()
            : editor
                ?.chain()
                .focus()
                .toggleHeading({ level: option.level as Level })
                .run();
        }}
        aria-selected={activeTextStyleOption?.level === option.level}
        subtext={option.meta ? option.meta : ''}
        isChecked={activeTextStyleOption?.level === option.level}
      >
        <span style={{ fontWeight: option.fontWeight }}>{option.label}</span>
      </Dropdown.ListItem>
    );
  });

  return (
    <Dropdown
      // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
      role="listbox"
      trigger={({ isOpen, ...triggerProps }) => (
        <Toolbar.Button
          aria-label="Select a text size"
          isPressed={isOpen}
          rightIcon={<Dropdown.Arrow />}
          {...triggerProps}
        >
          {dropdownPlaceholderLabel}
        </Toolbar.Button>
      )}
    >
      {textStyleDropdownItems}
    </Dropdown>
  );
}
